import { HStack, Input, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import ModalContainer from '../ModalContainer/ModalContainer';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notificationValidationSchema, TNotificationForm } from '@/validation/notification';
import CustomButton from '@/components/atoms/CustomButton';
import CheckBoxTile from '@/components/atoms/CheckBoxTile';
import {
  ENotificationChannels,
  ENotificationPriorities,
  ENotificationTypes,
  NOTIFICATION_TYPES,
} from '@/constants/stringVars';
import Dropdown from '@/components/atoms/Dropdown';

type Props = {
  isOpen: boolean;
  isLoading: boolean;
  title?: string;
  onClose?: () => void;
  onSubmit?: (form: TNotificationForm) => void;
};

const CreateNotificationModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  title = 'Send Notification',
}: Props) => {
  const methods = useForm({
    defaultValues: {
      type: ENotificationTypes.SYSTEM,
      notificationPriority: ENotificationPriorities.BASIC,
      channels: [ENotificationChannels.HTTP],
    },
    mode: 'onChange',
    resolver: yupResolver(notificationValidationSchema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors, touchedFields },
    setValue,
    watch,
  } = methods;

  const priority = watch('notificationPriority');
  const channels = watch('channels');
  const type = watch('type');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFormSubmit = (data: Record<string, any>) => {
    const formData = data as TNotificationForm;
    onSubmit?.(formData);
  };

  const onNotificationChannelClick = (channel: ENotificationChannels) => {
    const currentChannels = channels ?? [];
    if (currentChannels.includes(channel)) {
      const newChannels = currentChannels.filter((item) => item !== channel);
      methods.setValue('channels', newChannels);
    } else {
      const newChannels = [...currentChannels, channel];
      methods.setValue('channels', newChannels);
    }
  };

  const [isUrgentAlertModalVisible, setIsUrgentAlertModalVisible] = useState(false);

  const onNotificationPriorityClick = (priority: ENotificationPriorities) => {
    setValue('notificationPriority', priority);

    if (priority === ENotificationPriorities.URGENT) {
      setIsUrgentAlertModalVisible(true);
    }
  };

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} title={title}>
      <VStack width={'100%'}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)} style={{ width: '100%' }}>
            <VStack spacing={'20px'} width={'100%'}>
              <VStack width={'100%'} alignItems={'flex-start'}>
                <Input
                  placeholder="Title"
                  {...register('title')}
                  data-state={touchedFields.title ? (errors.title?.message ? 'invalid' : 'valid') : ''}
                  className="custom-input"
                />
                <Text color={'red.500'} fontSize={'14px'}>
                  {errors.title?.message ?? ''}
                </Text>
              </VStack>
              <VStack width={'100%'} alignItems={'flex-start'} spacing={0}>
                <Input
                  placeholder="Message"
                  {...register('message')}
                  data-state={touchedFields.message ? (errors.message?.message ? 'invalid' : 'valid') : ''}
                  as={'textarea'}
                  className="custom-input"
                  style={{ minHeight: '150px', maxHeight: '350px' }}
                />
                <Text color={'red.500'} fontSize={'14px'}>
                  {errors.message?.message ?? ''}
                </Text>
              </VStack>
              <VStack width={'100%'} alignItems={'flex-start'}>
                <Text>Channels</Text>
                <HStack width={'100%'} justifyContent={'space-between'}>
                  <CheckBoxTile
                    isChecked={channels?.includes(ENotificationChannels.EMAIL) ?? false}
                    width="100%"
                    onClick={() => onNotificationChannelClick(ENotificationChannels.EMAIL)}
                    checkboxOption={{
                      label: 'Email',
                      value: ENotificationChannels.EMAIL,
                    }}
                  />
                  <CheckBoxTile
                    isChecked={channels?.includes(ENotificationChannels.SMS) ?? false}
                    onClick={() => onNotificationChannelClick(ENotificationChannels.SMS)}
                    checkboxOption={{
                      label: 'SMS',
                      value: ENotificationChannels.SMS,
                    }}
                    width="100%"
                  />
                  <CheckBoxTile
                    isChecked={channels?.includes(ENotificationChannels.PUSH) ?? false}
                    onClick={() => onNotificationChannelClick(ENotificationChannels.PUSH)}
                    checkboxOption={{
                      label: 'Push',
                      value: ENotificationChannels.PUSH,
                    }}
                    width="100%"
                    isDisabled={false}
                  />
                  <CheckBoxTile
                    isChecked={channels?.includes(ENotificationChannels.HTTP) ?? false}
                    onClick={() => onNotificationChannelClick(ENotificationChannels.HTTP)}
                    checkboxOption={{
                      label: 'In-App',
                      value: ENotificationChannels.HTTP,
                    }}
                    width="100%"
                  />
                </HStack>
                <Text color={'red.500'} fontSize={'14px'}>
                  {errors.channels?.message ?? ''}
                </Text>
              </VStack>
              <VStack width={'100%'} alignItems={'flex-start'}>
                <Text>Notification Priority</Text>
                <HStack width={'100%'} justifyContent={'space-between'}>
                  <CheckBoxTile
                    isChecked={priority === ENotificationPriorities.BASIC}
                    width="100%"
                    onClick={() => onNotificationPriorityClick(ENotificationPriorities.BASIC)}
                    checkboxOption={{
                      label: 'Basic',
                      value: ENotificationPriorities.BASIC,
                    }}
                  />
                  <CheckBoxTile
                    isChecked={priority === ENotificationPriorities.HIGH}
                    width="100%"
                    onClick={() => onNotificationPriorityClick(ENotificationPriorities.HIGH)}
                    checkboxOption={{
                      label: 'High',
                      value: ENotificationPriorities.HIGH,
                    }}
                    borderColor="yellow.300"
                  />
                  <CheckBoxTile
                    isChecked={priority === ENotificationPriorities.URGENT}
                    width="100%"
                    onClick={() => onNotificationPriorityClick(ENotificationPriorities.URGENT)}
                    checkboxOption={{
                      label: 'Urgent',
                      value: ENotificationPriorities.URGENT,
                    }}
                    borderColor="red.500"
                  />
                </HStack>
                <Text color={'red.500'} fontSize={'14px'}>
                  {errors.notificationPriority?.message ?? ''}
                </Text>
              </VStack>
              <VStack width={'100%'} alignItems={'flex-start'}>
                <Text>Notification Type</Text>
                <Dropdown
                  title="Select One"
                  options={NOTIFICATION_TYPES}
                  width="100%"
                  popoverWidth="100%"
                  onOptionClick={(value) => setValue('type', value)}
                  selectedOption={type}
                  formatValues={true}
                />
              </VStack>

              <ModalContainer
                isOpen={isUrgentAlertModalVisible}
                onClose={() => setIsUrgentAlertModalVisible(false)}
                title="Bypass Notification Preferences"
                width="200px"
              >
                <Text textAlign={'center'}>
                  Urgent notification will bypass notification preferences and should only be used if the message is
                  critical.
                </Text>
              </ModalContainer>

              <HStack width={'100%'} justifyContent={'center'}>
                <CustomButton
                  width={'200px'}
                  label="Send"
                  buttonType="submit"
                  // disabled={!isAddObservationValid}
                  disabledColor="primary.500"
                  isLoading={isLoading}
                />
              </HStack>
            </VStack>
          </form>
        </FormProvider>
      </VStack>
    </ModalContainer>
  );
};

export default CreateNotificationModal;
